import React, { useEffect } from 'react';

interface OffcanvasProps {
  show?: boolean;
  onHide?: () => void;
  placement?: 'start' | 'end' | 'top' | 'bottom';
  className?: string;
  children?: React.ReactNode;
  role?: string;
  'aria-label'?: string;
}

const placementClasses: Record<string, string> = {
  start: 'left-0 top-0 h-full w-full max-w-md',
  end: 'right-0 top-0 h-full w-full max-w-md',
  top: 'top-0 left-0 w-full',
  bottom: 'bottom-0 left-0 w-full',
};

const hiddenTransform: Record<string, string> = {
  start: '-translate-x-full',
  end: 'translate-x-full',
  top: '-translate-y-full',
  bottom: 'translate-y-full',
};

function OffcanvasBase({ show, onHide, placement = 'start', className = '', children, ...rest }: OffcanvasProps) {
  useEffect(() => {
    if (show) {
      document.body.classList.add('offcanvas-open');
    } else {
      document.body.classList.remove('offcanvas-open');
    }
    return () => { document.body.classList.remove('offcanvas-open'); };
  }, [show]);

  const posClass = placementClasses[placement] || placementClasses.start;
  const transformClass = show ? 'translate-x-0 translate-y-0' : (hiddenTransform[placement] || hiddenTransform.start);

  return (
    <>
      {show && <div className="fixed inset-0 bg-black/50 z-40" onClick={onHide} />}
      <div
        className={`fixed z-50 bg-base-100 shadow-xl transition-transform duration-300 flex flex-col ${posClass} ${transformClass} ${className}`.trim()}
        tabIndex={-1}
        {...rest}
      >
        {children}
      </div>
    </>
  );
}

interface SubProps {
  className?: string;
  children?: React.ReactNode;
}

interface HeaderProps extends SubProps {
  closeButton?: boolean;
}

function OffcanvasHeader({ closeButton, className = '', children }: HeaderProps) {
  return (
    <div className={`flex items-center justify-between p-4 border-b border-base-300 ${className}`.trim()}>
      {children}
      {closeButton && <button type="button" className="btn btn-sm btn-circle btn-ghost" aria-label="Close"
        onClick={(e) => {
          const backdrop = e.currentTarget.closest('.fixed.z-50')?.previousElementSibling;
          (backdrop as HTMLElement)?.click();
        }}>✕</button>}
    </div>
  );
}

function OffcanvasTitle({ className = '', children, ...rest }: SubProps & { role?: string }) {
  return <h5 className={`text-lg font-semibold ${className}`.trim()} {...rest}>{children}</h5>;
}

function OffcanvasBody({ className = '', children }: SubProps) {
  return <div className={`p-4 flex-1 overflow-y-auto ${className}`.trim()}>{children}</div>;
}

const Offcanvas = Object.assign(OffcanvasBase, {
  Header: OffcanvasHeader,
  Title: OffcanvasTitle,
  Body: OffcanvasBody,
});

export default Offcanvas;
